<script lang="ts">
  /**
   * Full-viewport project lanes: Backlog | Planned | Active | Done.
   * Each open lane is a Focus-style column with a responsive card grid.
   * Collapsed lanes shrink to a rail so the remaining columns can densify.
   */
  import type { Project, Task } from '../lib/types';
  import {
    completeTask,
    deleteTask as deleteTaskApi,
    markTaskWontDo,
    updateTask,
  } from '../lib/taskApi';
  import { fetchProjectTasks, getCachedTasks } from '../lib/stores/projectTasksCache';
  import {
    canCollapseLane,
    groupProjectsByLane,
    laneGroupLabels,
    laneGridTemplate,
    PROJECT_LANES,
    type ProjectLane,
    type TaskPreview,
  } from '../lib/projectLanesUtils';
  import { setCompactProjectDragImage } from '../lib/dragImage';
  import { useProjectDragDrop } from '../lib/useProjectDragDrop.svelte';
  import ProjectPreviewCard from './ProjectPreviewCard.svelte';
  import TaskDetailPopover from './TaskDetailPopover.svelte';

  interface Props {
    projects: Project[];
    collapsedLanes?: ReadonlySet<ProjectLane>;
    /** Use the focused, tabbed lane presentation below the desktop breakpoint. */
    compact?: boolean;
    /** The single visible lane in the compact phone/tablet presentation. */
    compactActiveLane?: ProjectLane;
    /** Lets the page place the compact lane tabs alongside its other navigation. */
    showCompactSelector?: boolean;
    onSelectCompactLane?: (lane: ProjectLane) => void;
    onToggleLane?: (lane: ProjectLane) => void;
    onMoveProject?: (projectId: string, targetLane: ProjectLane) => void;
  }

  let {
    projects,
    collapsedLanes = new Set<ProjectLane>(),
    compact = false,
    compactActiveLane = 'backlog',
    showCompactSelector = true,
    onSelectCompactLane,
    onToggleLane,
    onMoveProject,
  }: Props = $props();

  const dnd = useProjectDragDrop((projectId, _project, dropKey) => {
    const lane = dropKey as ProjectLane;
    onMoveProject?.(projectId, lane);
    if (compact) onSelectCompactLane?.(lane);
  });

  let grouped = $derived(groupProjectsByLane(projects));
  let collapsedSet = $derived(
    collapsedLanes instanceof Set ? collapsedLanes : new Set<ProjectLane>(collapsedLanes ?? []),
  );
  let gridTemplate = $derived(laneGridTemplate(collapsedSet));
  let groupLabels = $derived(laneGroupLabels(collapsedSet));

  const laneAccents: Record<string, string> = {
    backlog: 'accent-slate',
    planned: 'accent-blue',
    active: 'accent-amber',
    done: 'accent-green',
  };

  let popoverTask = $state<Task | null>(null);
  let popoverAnchorEl = $state<HTMLElement | null>(null);
  let popoverProjectId = $state<string | null>(null);

  function tasksFor(projectId: string): Task[] {
    return getCachedTasks(projectId).tasks;
  }

  function handleToggleLane(lane: ProjectLane) {
    closePopover();
    onToggleLane?.(lane);
  }

  function handleCardDragStart(event: DragEvent, project: Project, lane: ProjectLane) {
    closePopover();
    dnd.handleDragStart(event, project, lane);
    setCompactProjectDragImage(event, project);
  }

  async function handleTaskClick(
    project: Project,
    preview: TaskPreview,
    event: MouseEvent | KeyboardEvent,
  ) {
    const anchor = event.currentTarget as HTMLElement;
    if (popoverTask?.id === preview.id) {
      closePopover();
      return;
    }

    let task = tasksFor(project.id).find((item) => item.id === preview.id) ?? null;
    if (!task) {
      await fetchProjectTasks(project.id);
      task = tasksFor(project.id).find((item) => item.id === preview.id) ?? null;
    }
    if (!task) return;

    popoverTask = task;
    popoverAnchorEl = anchor;
    popoverProjectId = project.id;
  }

  function closePopover() {
    popoverTask = null;
    popoverAnchorEl = null;
  }

  async function handlePopoverUpdate(task: Task, updates: Partial<Task>) {
    if (!popoverProjectId) return;
    const result = await updateTask(popoverProjectId, task.id, updates, tasksFor(popoverProjectId));
    if (result.success && popoverTask?.id === task.id) {
      popoverTask = result.tasks.find((item) => item.id === task.id) ?? { ...task, ...updates };
    }
  }

  async function handlePopoverDelete(task: Task, pendingUpdates?: Partial<Task>) {
    if (!popoverProjectId) return;
    if (pendingUpdates) await handlePopoverUpdate(task, pendingUpdates);
    closePopover();
    await deleteTaskApi(popoverProjectId, task.id, tasksFor(popoverProjectId));
  }

  async function handlePopoverWontDo(task: Task, pendingUpdates?: Partial<Task>) {
    if (!popoverProjectId) return;
    closePopover();
    await markTaskWontDo(popoverProjectId, task.id, tasksFor(popoverProjectId), pendingUpdates);
  }

  async function handlePopoverToggle(task: Task, pendingUpdates?: Partial<Task>) {
    if (!popoverProjectId) return;
    closePopover();
    await completeTask(popoverProjectId, task.id, tasksFor(popoverProjectId), pendingUpdates);
  }
</script>

{#if compact && showCompactSelector}
  <nav class="compact-lane-selector" aria-label="Project lanes">
    <div role="tablist" aria-label="Select project lane">
      {#each PROJECT_LANES as lane (lane.key)}
        <button
          type="button"
          role="tab"
          class:active={compactActiveLane === lane.key}
          class:drop-target={dnd.dropTarget === lane.key}
          aria-selected={compactActiveLane === lane.key}
          onclick={() => onSelectCompactLane?.(lane.key)}
          ondragover={(event) => dnd.handleDragOver(event, lane.key)}
          ondragleave={dnd.handleDragLeave}
          ondrop={(event) => dnd.handleDrop(event, lane.key)}>{lane.icon} {lane.label}</button
        >
      {/each}
    </div>
  </nav>
{/if}

<div class="lanes-view" style:grid-template-columns={gridTemplate}>
  {#if groupLabels.unscheduled}
    <div class="group-label unscheduled" aria-hidden="true">Unscheduled</div>
  {/if}
  {#if groupLabels.scheduled}
    <div class="group-label scheduled" aria-hidden="true">Scheduled</div>
  {/if}

  {#each PROJECT_LANES as lane (lane.key)}
    {@const laneProjects = grouped[lane.key]}
    {@const isCollapsed = !compact && collapsedSet.has(lane.key)}
    {@const canCollapse = canCollapseLane(collapsedSet, lane.key)}
    <section
      class="lane {laneAccents[lane.key]}"
      class:collapsed={isCollapsed}
      class:compact-inactive={compactActiveLane !== lane.key}
      class:drop-target={dnd.dropTarget === lane.key}
      aria-label="{lane.label} projects"
      ondragover={(event) => dnd.handleDragOver(event, lane.key)}
      ondragleave={dnd.handleDragLeave}
      ondrop={(event) => dnd.handleDrop(event, lane.key)}
    >
      {#if !compact}
        <header class="lane-header">
          <button
            type="button"
            class="lane-toggle"
            aria-expanded={!isCollapsed}
            aria-label={isCollapsed ? `Expand ${lane.label}` : `Collapse ${lane.label}`}
            title={isCollapsed
              ? `Expand ${lane.label}`
              : canCollapse
                ? `Collapse ${lane.label}`
                : 'Keep at least one column visible'}
            disabled={!isCollapsed && !canCollapse}
            onclick={() => handleToggleLane(lane.key)}
          >
            <span class="lane-icon">{lane.icon}</span>
            {#if isCollapsed}
              <span class="lane-count">{laneProjects.length}</span>
              <span class="lane-rail-label">{lane.label}</span>
            {:else}
              <h2 class="lane-title">{lane.label}</h2>
              <span class="lane-count">{laneProjects.length}</span>
              <span class="lane-chevron" aria-hidden="true">▾</span>
            {/if}
          </button>
        </header>
      {/if}
      {#if !isCollapsed}
        <div class="lane-cards">
          {#each laneProjects as project (project.id)}
            <ProjectPreviewCard
              {project}
              draggable={!!onMoveProject}
              dragging={dnd.draggingProjectId === project.id}
              onDragStart={(event) => handleCardDragStart(event, project, lane.key)}
              onDragEnd={dnd.handleDragEnd}
              onTaskClick={(preview, event) => handleTaskClick(project, preview, event)}
            />
          {/each}
          {#if laneProjects.length === 0}
            <div class="lane-empty">No projects</div>
          {/if}
        </div>
      {/if}
    </section>
  {/each}
</div>

<TaskDetailPopover
  task={popoverTask}
  anchorEl={popoverAnchorEl}
  project={projects.find((p) => p.id === popoverProjectId) ?? null}
  onClose={closePopover}
  onUpdate={handlePopoverUpdate}
  onDelete={handlePopoverDelete}
  onMarkWontDo={handlePopoverWontDo}
  onToggleComplete={handlePopoverToggle}
/>

<style>
  .compact-lane-selector {
    flex-shrink: 0;
    overflow-x: auto;
    padding: 0 0 0.125rem;
    scrollbar-width: none;
  }

  .compact-lane-selector::-webkit-scrollbar {
    display: none;
  }

  .compact-lane-selector [role='tablist'] {
    display: flex;
    gap: 0.375rem;
    width: max-content;
  }

  .compact-lane-selector button {
    flex: 0 0 auto;
    min-height: 2.25rem;
    padding: 0.375rem 0.625rem;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    background: white;
    color: #475569;
    font: inherit;
    font-size: 0.75rem;
    font-weight: 600;
  }

  .compact-lane-selector button.active {
    border-color: #93c5fd;
    background: #eff6ff;
    color: #1d4ed8;
  }

  .compact-lane-selector button.drop-target {
    box-shadow: 0 0 0 2px #3b82f6;
  }

  .lanes-view {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    grid-template-rows: auto minmax(0, 1fr);
    column-gap: 0.5rem;
    row-gap: 0.375rem;
    width: 100%;
    height: 100%;
    min-height: 0;
    min-width: 0;
  }

  .group-label {
    grid-row: 1;
    font-size: 0.5625rem;
    font-weight: 650;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #94a3b8;
    text-align: center;
    padding: 0 0.125rem;
    overflow: hidden;
    white-space: nowrap;
  }

  .group-label.unscheduled {
    grid-column: 1;
  }

  .group-label.scheduled {
    grid-column: 2 / 5;
    color: #64748b;
  }

  .lane {
    grid-row: 2;
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    background: white;
    border-radius: 0.375rem;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    overflow: hidden;
    position: relative;
    container-type: inline-size;
  }

  .lane::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: var(--lane-accent);
  }

  .accent-slate {
    --lane-accent: #94a3b8;
  }
  .accent-blue {
    --lane-accent: #3b82f6;
  }
  .accent-amber {
    --lane-accent: #f59e0b;
  }
  .accent-green {
    --lane-accent: #22c55e;
  }

  .lane.drop-target {
    box-shadow: 0 0 0 2px var(--lane-accent);
  }

  .lane-header {
    display: flex;
    align-items: stretch;
    flex-shrink: 0;
    border-bottom: 1px solid #f1f5f9;
  }

  .lane-toggle {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    width: 100%;
    margin: 0;
    padding: 0.5rem 0.5rem 0.375rem;
    border: none;
    background: transparent;
    cursor: pointer;
    text-align: left;
    color: inherit;
  }

  .lane-toggle:hover:not(:disabled) {
    background: #f8fafc;
  }

  .lane-toggle:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: -2px;
  }

  .lane-toggle:disabled {
    cursor: default;
  }

  .lane-title {
    flex: 1;
    min-width: 0;
    margin: 0;
    font-size: 0.6875rem;
    font-weight: 700;
    color: #334155;
  }

  .lane-icon {
    font-size: 0.75rem;
    line-height: 1;
    flex-shrink: 0;
  }

  .lane-count {
    font-size: 0.5625rem;
    font-weight: 600;
    color: #64748b;
    background: #f1f5f9;
    border-radius: 0.1875rem;
    padding: 0.0625rem 0.3125rem;
    min-width: 1rem;
    text-align: center;
    flex-shrink: 0;
  }

  .lane-chevron {
    font-size: 0.5rem;
    color: #94a3b8;
    flex-shrink: 0;
  }

  .lane-cards {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-auto-rows: min-content;
    align-content: start;
    gap: 0.375rem;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 0.5rem;
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 transparent;
  }

  @container (min-width: 28rem) {
    .lane-cards {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }

  @container (min-width: 38rem) {
    .lane-cards {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }
  }

  .lane-empty {
    grid-column: 1 / -1;
    font-size: 0.6875rem;
    color: #94a3b8;
    text-align: center;
    padding: 1.5rem 0.5rem;
    border: 1.5px dashed #e2e8f0;
    border-radius: 0.375rem;
    background: #fafbfc;
  }

  .lane.collapsed {
    container-type: normal;
  }

  .lane.collapsed .lane-header {
    flex: 1;
    min-height: 0;
    border-bottom: none;
  }

  .lane.collapsed .lane-toggle {
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    gap: 0.375rem;
    height: 100%;
    padding: 0.5rem 0.125rem 0.625rem;
  }

  .lane.collapsed .lane-count {
    padding: 0.0625rem 0.1875rem;
    min-width: 0;
  }

  .lane-rail-label {
    writing-mode: vertical-rl;
    transform: rotate(180deg);
    font-size: 0.625rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #64748b;
  }

  /* At compact widths a focused lane is more useful than four cramped columns. */
  @media (max-width: 1024px) {
    .lanes-view {
      grid-template-columns: minmax(0, 1fr) !important;
      grid-template-rows: minmax(0, 1fr);
      flex: 1;
    }

    .group-label,
    .lane.compact-inactive {
      display: none;
    }

    .lane {
      grid-column: 1;
      grid-row: 1;
    }

    .lane-cards {
      grid-template-columns: repeat(auto-fill, minmax(12rem, 1fr));
    }
  }
</style>
